import * as CL from '@design-system/component-library';
import { dsClass } from '../../common/constants/dsClasses';
import { t } from '../../common/i18n';
import { formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils';
import { InvitationStatus } from '../../generated/api/invitationStatus';
import { InvitationActionType, getInvitationActionButtons } from '../InvitationActionButton/InvitationActionButton';
import type { ContactInvitation } from '../../generated/api/contactInvitation';

const mapToDisclaimerProps = (invitation: ContactInvitation, contactEmail: string): CL.DisclaimerProps => {
  switch (invitation.status) {
    case InvitationStatus.Created:
      return {
        disclaimerType: 'warning',
        icon: <CL.Icon icon="redo" />,
        title: t.LDWQ('Sending invitation...'),
        text: t.NXUJ('Sending invitation to: {}', contactEmail),
        buttons: getInvitationActionButtons(invitation.invitationId, [InvitationActionType.CANCEL], 'secondary'),
      };
    case InvitationStatus.Sent:
      return {
        disclaimerType: 'warning',
        icon: <CL.Icon icon="clock" />,
        title: t.V082(`The invitation link is waiting for the user's confirmation`),
        text: t.PSDN(
          'An invitation link has been sent to the user by email. The link is active until {}.',
          formatTimestampToDDMMYYYY(invitation.expiresAt) ?? ''
        ),
        buttons: getInvitationActionButtons(invitation.invitationId, [InvitationActionType.CANCEL], 'secondary'),
      };
    case InvitationStatus.Expired:
      return {
        disclaimerType: 'alert',
        icon: <CL.Icon icon="warning" />,
        title: t.WJUQ(`Invitation expired`),
        text: t.WEWD(`The invitation link sent to the user's email has expired`),
        buttons: getInvitationActionButtons(
          invitation.invitationId,
          [InvitationActionType.RESEND, InvitationActionType.REMOVE],
          'secondary'
        ),
      };
    case InvitationStatus.Error:
      return {
        disclaimerType: 'alert',
        icon: <CL.Icon icon="warning" />,
        title: t.AANC(`Failed to send the invitation`),
        text: t.P4DI(
          'Failed to send the invitation to: {}. Please check the email address and try again.',
          contactEmail
        ),
        buttons: getInvitationActionButtons(
          invitation.invitationId,
          [InvitationActionType.RESEND, InvitationActionType.REMOVE],
          'secondary'
        ),
      };
  }
  return {};
};

export interface InvitationDisclaimerProps {
  invitation?: ContactInvitation;
  contactEmail?: string;
}

export const InvitationDisclaimer = ({ invitation, contactEmail }: InvitationDisclaimerProps) => {
  if (!invitation || invitation.status === InvitationStatus.Used || invitation.status === InvitationStatus.Cancelled) {
    return null;
  }
  return (
    <CL.Disclaimer
      {...mapToDisclaimerProps(invitation, contactEmail ?? '')}
      visible
      className={dsClass.MARGIN_VERTICAL_4}
    />
  );
};
